import { Auth0Client, User } from "@auth0/auth0-spa-js";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { DIAGRAMS_CACHE_NAME } from "../cache/cacheVersion";
import { getConfig } from "./config";

interface AuthContextValue {
  checkIsAuthenticated: () => Promise<boolean>;
  isLoading: boolean;
  user: User | null;
  getAccessTokenSilently: () => Promise<string>;
  logout: () => Promise<void>;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth0ClientRef = useRef<Auth0Client | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const initAuth0 = async () => {
      try {
        const { domain, clientId, audience } = getConfig();

        if (!domain || !clientId || !audience) {
          throw new Error("Missing Auth0 configuration");
        }

        const auth0 = new Auth0Client({
          domain: domain,
          clientId: clientId,
          authorizationParams: {
            redirect_uri: window.location.origin + "/auth-login-callback.html",
            ...(audience ? { audience: audience } : {}),
            scope: "openid profile email offline_access",
          },
          cacheLocation: "localstorage",
          useRefreshTokens: true,
        });

        auth0ClientRef.current = auth0;

        const authenticated = await auth0.isAuthenticated();

        if (authenticated) {
          const userData = await auth0.getUser();
          setUser(userData || null);
        }
      } catch (error) {
        console.error("Failed to initialize Auth0:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth0();
  }, []);

  const checkIsAuthenticated = useCallback(async (): Promise<boolean> => {
    if (!auth0ClientRef.current) {
      throw new Error("Auth0 client not initialized");
    }
    return await auth0ClientRef.current.isAuthenticated();
  }, []);

  const getAccessTokenSilently = useCallback(async (): Promise<string> => {
    if (!auth0ClientRef.current) {
      throw new Error("Auth0 client not initialized");
    }
    return await auth0ClientRef.current.getTokenSilently();
  }, []);

  const logout = useCallback((): Promise<void> => {
    const clearAuthData = (): void => {
      // Remove Auth0 cache entries from the taskpane's localStorage.
      // In Word Online the dialog and taskpane have separate partitioned
      // storage, so auth0.logout() in the dialog only clears its own copy.
      Object.keys(localStorage)
        .filter((key) => key.startsWith("@@auth0spajs@@"))
        .forEach((key) => localStorage.removeItem(key));

      caches.delete(DIAGRAMS_CACHE_NAME);
      setUser(null);
      window.location.reload();
    };

    const closeDialogAndClearAuth = (dialog: Office.Dialog): void => {
      dialog.close();
      clearAuthData();
    };

    return new Promise<void>((resolve, _reject) => {
      const logoutUrl = `${window.location.origin}/auth-logout.html`;

      Office.context.ui.displayDialogAsync(logoutUrl, { height: 40, width: 30 }, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to open logout dialog:", asyncResult.error.message);
          clearAuthData();
          resolve();
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          try {
            if ("message" in arg) {
              const data = JSON.parse(arg.message);

              if (data.error) {
                console.error("Logout error from dialog:", data.error);
              }
              closeDialogAndClearAuth(dialog);
              resolve();
            } else {
              console.error("Logout error code:", arg.error);
              closeDialogAndClearAuth(dialog);
              resolve();
            }
          } catch (error) {
            console.error("Error processing logout message:", error);
            closeDialogAndClearAuth(dialog);
            resolve();
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (_arg) => {
          closeDialogAndClearAuth(dialog);
          resolve();
        });
      });
    });
  }, []);

  const value: AuthContextValue = {
    checkIsAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
